import { ConditionalLink } from "components/conditionalLink";
import Loader from "components/DesignSystem/Loader/loader";
import { PreviewBody } from "components/PreviewModal";
import { useGetPreviewUrlQuery } from "store/apis/previewUrl";
import { Button } from "components/DesignSystem/Button/Button";
import { FinancialReviewFile } from "./FinancialReviewModal";
import { PreviewNotAvailable } from "components/icons/PreviewNotAvailable";
import Async from "components/DesignSystem/AsyncComponents/Async";
import {
  useGetFinancialClosingDocumentSummaryQuery,
  useUpdateFinancialClosingCheckListMutation,
} from "store/apis/financialClosing";
import { FinancialReviewAISummary } from "./FinancialReviewAISummary";
import { useCallback, useEffect, useRef } from "react";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { openDownloadUrl } from "util/openDownloadUrl";
import { useWindowSize } from "hooks/useWindowSize";

const FinancialReviewPreviewUnavailable = ({
  download_url,
  file_url,
}: {
  download_url?: string | null;
  file_url?: string | undefined;
}) => {
  const { isMobile } = useWindowSize();

  return (
    <div className="t-flex t-flex-col t-items-center t-justify-center">
      <div className="t-flex t-flex-col t-items-center t-mb-6">
        <PreviewNotAvailable />
        <div className="t-mt-4 t-text-body-lg t-text-text-30">
          Preview not available
        </div>
      </div>

      {download_url ? (
        <Button
          customType="secondary"
          onClick={() => openDownloadUrl(download_url)}
        >
          Download file
        </Button>
      ) : (
        <ConditionalLink to={file_url} target={isMobile ? "_self" : "_blank"}>
          {<Button customType="secondary">Visit link</Button>}
        </ConditionalLink>
      )}
    </div>
  );
};

export const FinancialReviewPreview = ({
  currentFile,
  groupId,
  entityId,
  financialClosingId,
  channelId,
  parentMessageId,
  setShowChannel,
  isViewOnly,
}: {
  currentFile: FinancialReviewFile;
  isViewOnly: boolean;
  groupId: string;
  entityId: string;
  financialClosingId: string;
  channelId: string;
  parentMessageId: string;
  setShowChannel: (show: boolean) => void;
}) => {
  const { alertToast } = useToast();
  const [updateFinancialClosingCheckList] =
    useUpdateFinancialClosingCheckListMutation();
  const attemptedFilesRef = useRef<Set<string>>(new Set());
  const fileId = currentFile.isClosingDocument
    ? currentFile.file_data?.uuid
    : currentFile.file_data?.document?.uuid;
  const { data: urlData, isLoading } = useGetPreviewUrlQuery(
    {
      groupId: groupId,
      fileId: fileId,
    },
    {
      skip: !currentFile?.file_data || !groupId || !fileId,
      pollingInterval: 50000,
    }
  );
  const { preview_url, download_url } = urlData || {};

  const { data: summaryData, isLoading: isSummaryLoading } =
    useGetFinancialClosingDocumentSummaryQuery(
      {
        entityId: entityId,
        financialClosingId: financialClosingId,
        documentId: currentFile.file_data ? currentFile.file_data?.uuid : "",
      },
      {
        skip:
          !currentFile.isClosingDocument ||
          !financialClosingId ||
          !entityId ||
          !currentFile.file_data,
      }
    );

  const isPreviewable =
    Boolean(preview_url) &&
    (Boolean(currentFile.file_data?.document?.is_previewable) ||
      Boolean(currentFile.file_data?.is_previewable));

  const onCheckListChange = useCallback(
    async ({ currentFile }: { currentFile: FinancialReviewFile }) => {
      try {
        await updateFinancialClosingCheckList({
          entityId: entityId,
          financialClosingId: financialClosingId,
          checklistItemId: currentFile.uuid,
          payload: {
            admin_resolved: true,
            document_id: currentFile.file_data?.uuid,
          },
        }).unwrap();
      } catch (error) {
        alertToast(
          {
            message: (error as BackendError).data?.error?.message,
          },
          error as Error
        );
      }
    },
    [entityId, financialClosingId, updateFinancialClosingCheckList, alertToast]
  );

  useEffect(() => {
    if (
      !isViewOnly &&
      currentFile &&
      currentFile.isClosingDocument &&
      !currentFile.resolved_by_admin &&
      !isViewOnly &&
      !attemptedFilesRef.current.has(currentFile.uuid)
    ) {
      attemptedFilesRef.current.add(currentFile.uuid);
      onCheckListChange({ currentFile });
    }
  }, [currentFile, isViewOnly, onCheckListChange]);

  return (
    <Async.Root
      isEmpty={false}
      isError={!isPreviewable}
      isSuccess={isPreviewable}
      isLoading={isLoading || isSummaryLoading}
      renderOnLoad={<Loader />}
    >
      <Async.ErrorHandler>
        <div className="t-h-[500px] t-w-full t-flex t-items-center t-justify-center">
          <FinancialReviewPreviewUnavailable
            download_url={download_url}
            file_url={currentFile.file_url}
          />
        </div>
      </Async.ErrorHandler>
      <Async.Success>
        <div className="t-flex t-flex-col t-gap-6 t-h-full t-w-full">
          {summaryData?.success && (
            <FinancialReviewAISummary
              currentFile={currentFile}
              entityId={entityId}
              channelId={channelId}
              parentMessageId={parentMessageId}
              setShowChannel={setShowChannel}
              summary={summaryData?.summary}
            />
          )}
          <div className="t-h-[1250px]">
            <PreviewBody
              file={
                currentFile.isClosingDocument
                  ? currentFile.file_data
                  : currentFile.file_data?.document
              }
              preview_url={preview_url!}
              fullWidth
            />
          </div>
        </div>
      </Async.Success>
    </Async.Root>
  );
};
